import type { ReactNode } from 'react'
import type { NextPageWithLayout, TeamMember, TeamRole } from '@/types'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
    Check,
    Ellipsis,
    MailQuestion,
    Minus,
    ShieldCheck,
    UserMinus,
    UserPlus,
    UsersRound
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatRelative } from '@/lib/date'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/dashboard/page-header'
import { SectionCard } from '@/components/dashboard/section-card'
import { EntityAvatar } from '@/components/dashboard/entity-avatar'
import { MemberStatusBadge } from '@/components/dashboard/status-badge'
import { ProgressBar } from '@/components/dashboard/progress-bar'
import { ConfirmDialog } from '@/components/dashboard/confirm-dialog'
import { InviteMemberDialog } from '@/components/team/invite-member-dialog'
import { ASSIGNABLE_ROLES, PERMISSIONS, TEAM_MEMBERS, WORKSPACE } from '@/data/team'
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '@/constants/dashboard'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table'

const ROLE_ORDER: TeamRole[] = ['owner', 'admin', 'finance', 'support', 'viewer']

interface MemberMenuProps {
    member: TeamMember
    onChangeRole: (member: TeamMember, role: TeamRole) => void
    onRemove: (member: TeamMember) => void
}

const MemberMenu = ({ member, onChangeRole, onRemove }: MemberMenuProps): ReactNode => {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger render={<Button variant='ghost' size='icon-sm' aria-label={`Aksi untuk ${member.name}`} />}>
                <Ellipsis />
            </DropdownMenuTrigger>

            <DropdownMenuContent align='end' className='w-56'>
                <DropdownMenuGroup>
                    <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                            <ShieldCheck />
                            Ubah peran
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className='w-48'>
                            <DropdownMenuGroup>
                                <DropdownMenuLabel>Peran {member.name.split(' ')[0]}</DropdownMenuLabel>
                                <DropdownMenuRadioGroup
                                    value={member.role}
                                    onValueChange={(value) => onChangeRole(member, value as TeamRole)}
                                >
                                    {ASSIGNABLE_ROLES.map((role) => (
                                        <DropdownMenuRadioItem key={role} value={role}>
                                            {ROLE_LABELS[role]}
                                        </DropdownMenuRadioItem>
                                    ))}
                                </DropdownMenuRadioGroup>
                            </DropdownMenuGroup>
                        </DropdownMenuSubContent>
                    </DropdownMenuSub>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />

                <DropdownMenuGroup>
                    <DropdownMenuItem variant='destructive' onClick={() => onRemove(member)}>
                        <UserMinus />
                        {member.status === 'invited' ? 'Batalkan undangan' : 'Hapus dari tim'}
                    </DropdownMenuItem>
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

const TeamPage: NextPageWithLayout = (): ReactNode => {
    const router = useRouter()
    const [members, setMembers] = useState<TeamMember[]>(TEAM_MEMBERS)
    const [inviteOpen, setInviteOpen] = useState(false)
    const [removing, setRemoving] = useState<TeamMember | null>(null)

    useEffect(() => {
        if (!router.isReady || router.query.undang !== '1') return

        setInviteOpen(true)
        router.replace('/tim', undefined, { shallow: true })
    }, [router, router.isReady, router.query])

    const active = members.filter((member) => member.status === 'active').length
    const invited = members.filter((member) => member.status === 'invited').length
    const used = members.length
    const seatsLeft = Math.max(0, WORKSPACE.seats - used)
    const emails = useMemo(() => new Set(members.map((member) => member.email.toLowerCase())), [members])

    const sortedMembers = useMemo(() => [...members].sort((a, b) => (
        ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) || (a.status === b.status ? 0 : a.status === 'active' ? -1 : 1)
    )), [members])

    const changeRole = (member: TeamMember, role: TeamRole): void => {
        if (member.role === role) return

        setMembers((list) => list.map((item) => (item.id === member.id ? { ...item, role } : item)))
        toast.success('Peran diperbarui', { description: `${member.name} sekarang berperan sebagai ${ROLE_LABELS[role]}.` })
    }

    return (
        <>
            <Seo title='Tim' description='Anggota tim, peran, dan undangan di workspace Arunika.' />

            <div className='space-y-6'>
                <PageHeader
                    title='Tim'
                    description='Atur siapa saja yang bisa membuka dasbor Arunika dan apa yang boleh mereka ubah.'
                    actions={(
                        <Button size='lg' onClick={() => setInviteOpen(true)} disabled={seatsLeft === 0}>
                            <UserPlus data-icon='inline-start' />
                            Undang anggota
                        </Button>
                    )}
                />

                <section aria-label='Ringkasan tim' className='grid gap-4 sm:grid-cols-3'>
                    <div className='flex items-start gap-3 rounded-xl border bg-card p-5'>
                        <span className='flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground' aria-hidden='true'>
                            <UsersRound className='size-[18px]' />
                        </span>
                        <div>
                            <p className='text-sm text-muted-foreground'>Anggota aktif</p>
                            <p className='mt-1 font-heading text-xl font-semibold text-foreground tabular-nums'>{active}</p>
                        </div>
                    </div>

                    <div className='flex items-start gap-3 rounded-xl border bg-card p-5'>
                        <span className='flex size-9 shrink-0 items-center justify-center rounded-lg bg-info/12 text-info' aria-hidden='true'>
                            <MailQuestion className='size-[18px]' />
                        </span>
                        <div>
                            <p className='text-sm text-muted-foreground'>Undangan menunggu</p>
                            <p className='mt-1 font-heading text-xl font-semibold text-foreground tabular-nums'>{invited}</p>
                        </div>
                    </div>

                    <div className='rounded-xl border bg-card p-5'>
                        <div className='flex items-baseline justify-between gap-3'>
                            <p className='text-sm text-muted-foreground'>Kursi terpakai</p>
                            <p className='font-heading text-xl font-semibold text-foreground tabular-nums'>
                                {used}
                                <span className='text-sm font-medium text-muted-foreground'> / {WORKSPACE.seats}</span>
                            </p>
                        </div>
                        <ProgressBar className='mt-3' value={(used / WORKSPACE.seats) * 100} label='Kursi terpakai' />
                        <p className='mt-2 text-xs text-muted-foreground'>
                            {seatsLeft > 0 ? `${seatsLeft} kursi masih tersedia` : 'Semua kursi terpakai'}
                        </p>
                    </div>
                </section>

                <SectionCard
                    title='Anggota'
                    description='Undangan yang belum diterima tetap memakai satu kursi.'
                    contentClassName='px-0 pb-2'
                >
                    <Table>
                        <TableHeader>
                            <TableRow className='bg-muted/40 hover:bg-muted/40'>
                                <TableHead className='pl-5 text-xs font-medium text-muted-foreground'>Anggota</TableHead>
                                <TableHead className='hidden text-xs font-medium text-muted-foreground md:table-cell'>Jabatan</TableHead>
                                <TableHead className='text-xs font-medium text-muted-foreground'>Peran</TableHead>
                                <TableHead className='hidden text-xs font-medium text-muted-foreground sm:table-cell'>Status</TableHead>
                                <TableHead className='hidden text-xs font-medium text-muted-foreground lg:table-cell'>Terakhir aktif</TableHead>
                                <TableHead className='w-12 pr-5'>
                                    <span className='sr-only'>Aksi</span>
                                </TableHead>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {sortedMembers.map((member) => (
                                <TableRow key={member.id}>
                                    <TableCell className='py-3 pl-5'>
                                        <div className='flex min-w-0 items-center gap-3'>
                                            <EntityAvatar name={member.name} className='rounded-full' />
                                            <div className='min-w-0'>
                                                <p className='truncate font-medium text-foreground'>
                                                    {member.name}
                                                    {member.role === 'owner' && <span className='ml-1.5 text-xs font-normal text-muted-foreground'>(Anda)</span>}
                                                </p>
                                                <p className='truncate text-xs text-muted-foreground'>{member.email}</p>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className='hidden text-muted-foreground md:table-cell'>{member.title}</TableCell>
                                    <TableCell>
                                        <span className={cn(
                                            'inline-flex h-6 items-center gap-1 rounded-md border px-2 text-xs font-medium',
                                            member.role === 'owner' ? 'border-brand/30 bg-brand/10 text-foreground' : 'bg-card text-foreground'
                                        )}>
                                            {member.role === 'owner' && <ShieldCheck className='size-3.5 text-brand' aria-hidden='true' />}
                                            {ROLE_LABELS[member.role]}
                                        </span>
                                    </TableCell>
                                    <TableCell className='hidden sm:table-cell'>
                                        <MemberStatusBadge status={member.status} />
                                    </TableCell>
                                    <TableCell className='hidden text-muted-foreground lg:table-cell'>
                                        {member.status === 'invited' && member.invitedAt
                                            ? `Diundang ${formatRelative(member.invitedAt).toLowerCase()}`
                                            : member.lastActiveAt ? formatRelative(member.lastActiveAt) : 'Belum pernah'}
                                    </TableCell>
                                    <TableCell className='pr-5 text-right'>
                                        {member.role === 'owner'
                                            ? <span className='sr-only'>Pemilik tidak dapat diubah</span>
                                            : <MemberMenu member={member} onChangeRole={changeRole} onRemove={setRemoving} />}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SectionCard>

                <SectionCard
                    title='Peran dan izin'
                    description='Apa saja yang bisa dilakukan setiap peran di workspace ini.'
                    contentClassName='px-0 pb-2'
                >
                    <div className='overflow-x-auto'>
                        <table className='w-full min-w-[640px] text-sm'>
                            <caption className='sr-only'>Daftar izin untuk setiap peran</caption>
                            <thead>
                                <tr className='border-y bg-muted/40 text-xs text-muted-foreground'>
                                    <th scope='col' className='px-5 py-2.5 text-left font-medium'>Izin</th>
                                    {ROLE_ORDER.map((role) => (
                                        <th key={role} scope='col' className='px-3 py-2.5 text-center font-medium'>
                                            {ROLE_LABELS[role]}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className='divide-y'>
                                {PERMISSIONS.map((permission) => (
                                    <tr key={permission.label}>
                                        <th scope='row' className='px-5 py-3 text-left font-normal text-foreground'>{permission.label}</th>
                                        {ROLE_ORDER.map((role) => {
                                            const allowed = permission.roles.includes(role)

                                            return (
                                                <td key={role} className='px-3 py-3 text-center'>
                                                    {allowed
                                                        ? <Check className='mx-auto size-4 text-success' aria-hidden='true' />
                                                        : <Minus className='mx-auto size-4 text-muted-foreground/50' aria-hidden='true' />}
                                                    <span className='sr-only'>{allowed ? 'Diizinkan' : 'Tidak diizinkan'}</span>
                                                </td>
                                            )
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <dl className='grid gap-3 px-5 pt-4 pb-3 sm:grid-cols-2 lg:grid-cols-3'>
                        {ROLE_ORDER.map((role) => (
                            <div key={role} className='rounded-lg border bg-muted/30 p-3'>
                                <dt className='text-sm font-medium text-foreground'>{ROLE_LABELS[role]}</dt>
                                <dd className='mt-0.5 text-xs leading-relaxed text-muted-foreground'>{ROLE_DESCRIPTIONS[role]}</dd>
                            </div>
                        ))}
                    </dl>
                </SectionCard>
            </div>

            <InviteMemberDialog
                open={inviteOpen}
                onOpenChange={setInviteOpen}
                seatsLeft={seatsLeft}
                isEmailTaken={(email) => emails.has(email)}
                onInvite={(member) => {
                    setMembers((list) => [...list, member])
                    toast.success('Undangan dibuat', { description: `${member.email} akan bergabung sebagai ${ROLE_LABELS[member.role]}.` })
                }}
            />

            <ConfirmDialog
                open={removing !== null}
                onOpenChange={(open) => {
                    if (!open) setRemoving(null)
                }}
                title={removing?.status === 'invited' ? 'Batalkan undangan?' : 'Hapus anggota dari tim?'}
                description={removing
                    ? removing.status === 'invited'
                        ? `Undangan untuk ${removing.email} tidak bisa dipakai lagi dan kursinya kembali tersedia.`
                        : `${removing.name} langsung kehilangan akses ke workspace Arunika. Kursinya kembali tersedia.`
                    : ''}
                confirmLabel={removing?.status === 'invited' ? 'Ya, batalkan' : 'Ya, hapus'}
                destructive
                onConfirm={() => {
                    if (!removing) return
                    setMembers((list) => list.filter((item) => item.id !== removing.id))
                    toast.success(removing.status === 'invited' ? 'Undangan dibatalkan' : `${removing.name} dihapus dari tim`)
                }}
            />
        </>
    )
}

export default TeamPage
